import { FunctionComponent, useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import Head from 'next/head'
import Script from 'next/script'

import config from '@config'
import usePageMetadata from '@hooks/usePageMetadata'
import useSubscriptionStatus from '@hooks/useSubscriptionStatus'
import useTiunSession from '@hooks/useTiunSession'
import useTiunTrackingEvents from '@hooks/useTiunTrackingEvents'
import constants from '@components/Tiun/constants'
import { TiunInteractionType } from '@components/Tiun/types'
import {
  updatePianoExperience,
  updateUserConnectedState,
} from '@components/Tiun/utils'

const {
  TIUN_HIDE_PAYWALL,
  TIUN_SHOW_PAYWALL,
  TIUN_SNIPPET_INITIALIZED,
  TIUN_SNIPPET_INTERACTION,
  TIUN_UPDATE_CONTENT,
  TIUN_EVENT,
} = constants

const {
  tiun: { scriptUrl, snippetCSSUrl },
} = config

const Tiun: FunctionComponent = () => {
  const { id, isPlus } = usePageMetadata()
  const queryClient = useQueryClient()
  const { isTiunSessionActive } = useTiunSession()
  const { subscriptionStatus } = useSubscriptionStatus()
  const {
    onPaidTimeStart,
    onPaidTimeEnd,
    onStartCheckout,
    onTiunStartSessionClick,
    onTiunClick,
    onTiunClickOffer,
  } = useTiunTrackingEvents()

  const updateContentInfo = useCallback(
    (id?: string) => {
      window.postMessage(
        {
          type: TIUN_EVENT,
          content: TIUN_UPDATE_CONTENT,
          contentId: id,
          contentType: isPlus ? 'active' : 'inactive',
        },
        window.location.origin,
      )
    },
    [isPlus],
  )

  const handleShowPaywall = useCallback(
    (isUserConnectedWithTiun: boolean) => {
      updateUserConnectedState(queryClient, isUserConnectedWithTiun)
      if (isTiunSessionActive) {
        updatePianoExperience({
          isTiunSessionActive: false,
          queryClient,
        })
        onPaidTimeEnd()
      }
    },
    [onPaidTimeEnd, queryClient, isTiunSessionActive],
  )

  const handleHidePaywall = useCallback(() => {
    updatePianoExperience({
      isTiunSessionActive: true,
      queryClient,
    })

    onPaidTimeStart()
  }, [onPaidTimeStart, queryClient])

  const handleTiunSnippetInteraction = useCallback(
    (interactionType: TiunInteractionType) => {
      if (['ShowQrCode', 'Connect'].includes(interactionType)) {
        onStartCheckout(interactionType)
      } else if (interactionType === 'ShowConnectionDetails') {
        onTiunClickOffer()
      } else if (interactionType === 'StartSession') {
        onTiunStartSessionClick()
      } else {
        onTiunClick(interactionType)
      }
    },
    [onStartCheckout, onTiunClickOffer, onTiunStartSessionClick, onTiunClick],
  )

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type === TIUN_EVENT) {
        if (!event.data?.content) {
          return
        }

        if (event.data.content === TIUN_SNIPPET_INITIALIZED) {
          updateContentInfo(id)
        }

        if (event.data.content === TIUN_HIDE_PAYWALL) {
          handleHidePaywall()
        }

        if (event.data.content === TIUN_SHOW_PAYWALL) {
          const isUserConnectedWithTiun = event.data.isConnected
          handleShowPaywall(isUserConnectedWithTiun)
        }

        if (event.data.content === TIUN_SNIPPET_INTERACTION) {
          const interactionType = event.data
            .interactionType as TiunInteractionType
          handleTiunSnippetInteraction(interactionType)
        }
      }
    },
    [
      handleHidePaywall,
      handleShowPaywall,
      handleTiunSnippetInteraction,
      id,
      updateContentInfo,
    ],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  useEffect(() => {
    updateContentInfo(id)
  }, [id, updateContentInfo, isTiunSessionActive, queryClient])

  return !['subscribed', undefined].includes(subscriptionStatus) ? (
    <>
      <Head>
        <link rel="stylesheet" href={snippetCSSUrl} />
      </Head>

      <Script src={scriptUrl} />
    </>
  ) : null
}

export default Tiun
